import React, { useState, useEffect, useRef } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { FaMicrophone } from "react-icons/fa";
import { BsRecord2 } from "react-icons/bs";
import { RiSendPlaneFill } from "react-icons/ri";
import { getTime } from "./logic/getTime";
import { generateUUID } from "./logic/generateUUID";
import useAutosizeTextArea from "../commons/useAutosizeTextArea";
import { Message } from "../../types/chat";  // Import shared Message type

interface MessageInputProps {
  addUserMessage: (message: Message) => void;
  conversationId?: number;
}

const MessageInput = ({ addUserMessage, conversationId }: MessageInputProps) => {
  const conversationLanguage: string | null = localStorage.getItem("conversation_language");
  const [newMessage, setNewMessage] = useState<string>("");
  const [listening, setListening] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { transcript, resetTranscript, browserSupportsSpeechRecognition } =
    useSpeechRecognition();

  useEffect(() => {
    if (listening && transcript) {
      // The transcript is cumulative, so just set it directly
      // react-speech-recognition already accumulates all words spoken
      setNewMessage(transcript);
    }
  }, [transcript, listening]);

  useAutosizeTextArea(textAreaRef.current, newMessage);

  const stopListening = async (): Promise<void> => {
    try {
      await SpeechRecognition.stopListening();
      setListening(false);
      // Keep the final transcript in the message
      // Don't reset here - let the user keep what was transcribed
    } catch (error) {
      console.error('Error stopping speech recognition:', error);
      setListening(false);
    }
  };

  const startListening = async (): Promise<void> => {
    if (!browserSupportsSpeechRecognition) {
      alert('Your browser does not support speech recognition. Please use Chrome, Edge, or Safari.');
      return;
    }

    try {
      // Reset transcript before starting new session
      resetTranscript();
      await SpeechRecognition.startListening({
        language: conversationLanguage || undefined,
        continuous: true
      });
      setListening(true);
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      alert('Failed to start speech recognition. Please check microphone permissions.');
      setListening(false);
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    if (listening) await stopListening();

    const messageToSend: string = newMessage.trim().replace(/[\r\n]+/gm, ''); // Trim and remove line breaks

    if (messageToSend !== '') {
      // Use prop conversationId if available, otherwise fall back to localStorage
      const effectiveConversationId = conversationId || parseInt(localStorage.getItem("conversation_id") || "0", 10);

      addUserMessage({
        role: "user",
        content: messageToSend,
        dateTime: getTime(),
        audioUrl: null,
        message_id: generateUUID(),
        conversation_id: effectiveConversationId,
      });
      setNewMessage('');
      resetTranscript(); // Clear transcript after sending message
    }
  };

  const handleKeyPress = async (e: React.KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleSendMessage();
    }
  };

  const handleTextChange = async (e: React.ChangeEvent<HTMLTextAreaElement>): Promise<void> => {
    if (listening) {
      // stop listening if the user edits the text manually
      await stopListening();
      resetTranscript(); // Clear transcript when user manually edits
    }
    setNewMessage(e.target.value);
  };

  return (
    <div className="flex">
      <div className="flex flex-grow relative justify-end">
        <textarea
          className="flex-grow resize-none p-3 rounded-3xl"
          value={newMessage}
          ref={textAreaRef}
          onChange={(e) => handleTextChange(e)}
          onKeyPress={handleKeyPress}
          placeholder="Mensaje..."
        ></textarea>
        {browserSupportsSpeechRecognition && (
          <div className="absolute right-0 bottom-0">
            {listening ? (
              <button
                onClick={stopListening}
                className="btn btn-circle bg-info border-none outline-none"
                style={{ cursor: "pointer" }}
              >
                <BsRecord2 size={30} />
              </button>
            ) : (
              <button
                onClick={startListening}
                className="btn btn-circle bg-info border-none outline-none"
                style={{ cursor: "pointer" }}
              >
                <FaMicrophone size={30} />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-end">
        <button
          onClick={handleSendMessage}
          className="btn btn-circle bg-info border-none outline-none"
          style={{ cursor: "pointer" }}
        >
          <RiSendPlaneFill size={30} />
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
